import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Clock, Filter, Info, X, ChevronLeft, MessageSquare, AlertCircle, BarChart3, Target, TrendingDown, ChevronRight, RefreshCw } from 'lucide-react';
import { log } from '@/utils/logUtils';

const NotificationPanel = ({ onClose, onActionComplete }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('main'); // 'main', 'detail', 'action'
  const [activeNotification, setActiveNotification] = useState(null);
  const [actionStep, setActionStep] = useState(1);
  const [filter, setFilter] = useState('all');
  const [isProcessing, setIsProcessing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Mock notification data aligned with existing analytics and KPIs
  const notifications = [
    {
      id: 1,
      type: 'alert',
      title: 'Campaign performance alert',
      message: 'Summer Fuel Promotion ROI below target by 8.3%',
      timestamp: '15 minutes ago',
      severity: 'high',
      unread: true,
      icon: <Target size={16} className="text-red-500" />,
      category: 'campaign',
      actionPath: '/campaign-management',
      relatedKPI: 'Campaign ROI',
      relatedKPIValue: '1.1x vs 1.75x target',
      detailData: {
        campaignId: 'CAMP-2023-07',
        expectedROI: '1.75x',
        actualROI: '1.1x',
        underperformingLocations: ['Bangkok Central', 'Pattaya Beach', 'Chiang Mai North'],
        recommendedActions: [
          'Adjust digital ad targeting in underperforming regions',
          'Increase in-store promotion visibility',
          'Consider extending offer validity period by 2 weeks'
        ],
        actionWorkflow: {
          steps: [
            {
              title: 'Adjust campaign targeting',
              description: 'Update digital ad targeting parameters for underperforming regions',
              fields: [
                { label: 'Target audience', type: 'select', options: ['All customers', 'Premium segment', 'Regular customers', 'New customers'] },
                { label: 'Location focus', type: 'select', options: ['All locations', 'Bangkok Central', 'Pattaya Beach', 'Chiang Mai North'] },
                { label: 'Budget reallocation', type: 'range', min: 0, max: 30, value: 15, unit: '%' }
              ]
            },
            {
              title: 'Promotion visibility',
              description: 'Enhance in-store promotion visibility measures',
              fields: [
                { label: 'Signage location', type: 'select', options: ['Entrance', 'Point of sale', 'Fuel pumps', 'All locations'] },
                { label: 'Staff communication', type: 'checkbox', checked: true, label: 'Brief staff on active promotion details' },
                { label: 'Digital displays', type: 'checkbox', checked: false, label: 'Update digital screen content' }
              ]
            },
            {
              title: 'Campaign timeframe',
              description: 'Review and adjust campaign duration',
              fields: [
                { label: 'Extend campaign', type: 'radio', options: ['No extension', '1 week', '2 weeks', '4 weeks'] },
                { label: 'Notification to customers', type: 'checkbox', checked: true, label: 'Send update to enrolled customers' }
              ]
            }
          ]
        }
      }
    },
    {
      id: 2,
      type: 'competitor',
      title: 'Competitor price change',
      message: 'ABC Gas reduced prices in 28 areas by an average of 0.15 ฿/L',
      timestamp: '2 hours ago',
      severity: 'medium',
      unread: true,
      icon: <TrendingDown size={16} className="text-amber-500" />,
      category: 'pricing',
      actionPath: '/competitor-360',
      relatedKPI: 'Volume',
      relatedKPIValue: 'Projected -3.7% impact',
      detailData: {
        competitor: 'ABC Gas',
        priceDecrease: '0.15 ฿/L average',
        affectedAreas: 28,
        targetedProducts: ['Regular 91', 'Premium 95'],
        marketImpact: 'Moderate',
        recommendedResponse: [
          'Evaluate price matching in 12 key urban locations',
          'Activate loyalty bonus points for affected areas',
          'Monitor volume impact trends over next 48 hours'
        ],
        actionWorkflow: {
          steps: [
            {
              title: 'Price response strategy',
              description: 'Select approach to competitor price changes',
              fields: [
                { label: 'Response type', type: 'select', options: ['No action', 'Selective matching', 'Full matching', 'Alternative promotion'] },
                { label: 'Target locations', type: 'multiselect', options: ['Bangkok Central', 'Bangkok East', 'Pattaya', 'Rayong', 'Chiang Mai'], selected: ['Bangkok Central', 'Pattaya'] },
                { label: 'Products affected', type: 'multiselect', options: ['Regular 91', 'Premium 95', 'Premium 97', 'Diesel'], selected: ['Regular 91', 'Premium 95'] }
              ]
            },
            {
              title: 'Loyalty promotion',
              description: 'Configure loyalty point bonus for affected areas',
              fields: [
                { label: 'Bonus points', type: 'range', min: 0, max: 5, value: 2, unit: 'x multiplier' },
                { label: 'Duration', type: 'select', options: ['3 days', '1 week', '2 weeks'] },
                { label: 'Customer targeting', type: 'select', options: ['All customers', 'Loyalty members only', 'Premium segment'] }
              ]
            },
            {
              title: 'Implementation schedule',
              description: 'Set timing for price response actions',
              fields: [
                { label: 'Price changes effective', type: 'select', options: ['Immediately', 'Next day', 'Next week'] },
                { label: 'Notify station managers', type: 'checkbox', checked: true },
                { label: 'Market communication', type: 'checkbox', checked: true }
              ]
            }
          ]
        }
      }
    },
    {
      id: 3,
      type: 'system',
      title: 'System update scheduled',
      message: 'Platform maintenance scheduled for tomorrow at 2am (2 hour window)',
      timestamp: '5 hours ago',
      severity: 'low',
      unread: true,
      icon: <Info size={16} className="text-blue-500" />,
      category: 'system',
      actionPath: null,
      relatedKPI: null,
      detailData: {
        maintenanceWindow: 'May 18, 2:00 AM - 4:00 AM ICT',
        impactedSystems: ['Reporting Engine', 'Campaign Analytics', 'Price Optimization'],
        backupAvailability: 'Read-only access to critical dashboards',
        contactInfo: 'IT Support Hotline: +66 2 123 4567'
      }
    },
    {
      id: 4,
      type: 'performance',
      title: 'Performance milestone reached',
      message: 'Q2 volume target achieved for Urban Premium segment',
      timestamp: '1 day ago',
      severity: 'info',
      unread: false,
      icon: <BarChart3 size={16} className="text-green-500" />,
      category: 'performance',
      actionPath: '/executive-dashboard',
      relatedKPI: 'Urban Premium Volume',
      relatedKPIValue: '103% of target',
      detailData: {
        segment: 'Urban Premium',
        achievement: '103% of Q2 target',
        topPerformers: ['Sukhumvit 24', 'Silom Complex', 'Wireless Road'],
        growth: '+12.7% year-over-year',
        bonusStatus: 'Team incentive bonus activated',
        actionWorkflow: {
          steps: [
            {
              title: 'Performance recognition',
              description: 'Acknowledge team achievement and distribute rewards',
              fields: [
                { label: 'Recognition type', type: 'select', options: ['Team celebration', 'Individual awards', 'Both'] },
                { label: 'Bonus distribution', type: 'select', options: ['Immediate release', 'End of month', 'With next payroll'] },
                { label: 'Recognition announcement', type: 'checkbox', checked: true, label: 'Send company-wide announcement' }
              ]
            },
            {
              title: 'Insights capture',
              description: 'Document successful strategies for knowledge sharing',
              fields: [
                { label: 'Success factors', type: 'multiselect', options: ['Marketing approach', 'Staff engagement', 'Loyalty program', 'Store experience', 'Product mix'], selected: ['Marketing approach', 'Loyalty program'] },
                { label: 'Schedule knowledge sharing', type: 'select', options: ['Next week', 'Next month', 'Next quarter'] }
              ]
            },
            {
              title: 'Set new targets',
              description: 'Establish follow-up objectives for Q3',
              fields: [
                { label: 'Q3 target increase', type: 'range', min: 0, max: 10, value: 5, unit: '%' },
                { label: 'Focus areas', type: 'multiselect', options: ['Volume growth', 'Margin improvement', 'Market share', 'Customer satisfaction'], selected: ['Volume growth', 'Margin improvement'] }
              ]
            }
          ]
        }
      }
    }
  ];
  
  // Filter notifications based on category
  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications.filter(note => note.category === filter);

  // Refresh notifications after an action is complete
  useEffect(() => {
    if (refreshTrigger > 0) {
      // In a real app, this would fetch new notifications from the server
      log('Refreshing notifications after action completed');
    }
  }, [refreshTrigger]);

  const handleNotificationClick = (notification) => {
    setActiveNotification(notification);
    setActiveView('detail');
  };

  const handleBack = () => {
    if (activeView === 'action') {
      setActiveView('detail');
      setActionStep(1);
    } else {
      setActiveView('main');
      setActiveNotification(null);
    }
  };

  const handleActionStart = () => {
    setActiveView('action');
    setActionStep(1);
  };

  const handleNextStep = () => {
    const maxSteps = activeNotification?.detailData?.actionWorkflow?.steps?.length || 1;
    if (actionStep < maxSteps) {
      setActionStep(actionStep + 1);
    } else {
      // Final step - complete the action
      completeAction();
    }
  };

  const handlePrevStep = () => {
    if (actionStep > 1) {
      setActionStep(actionStep - 1);
    }
  };

  const completeAction = () => {
    setIsProcessing(true);
    
    // Simulate API call to complete the action
    setTimeout(() => {
      // In a real app, this would update the notification status and trigger a refresh
      log('Action completed for notification:', activeNotification.id);
      
      // Mark notification as read
      const updatedNotifications = notifications.map(note => 
        note.id === activeNotification.id ? {...note, unread: false} : note
      );
      
      setIsProcessing(false);
      setActiveView('main');
      setActiveNotification(null);
      setActionStep(1);
      setRefreshTrigger(prev => prev + 1);
      
      // Notify parent component that action is complete
      if (onActionComplete) {
        onActionComplete(activeNotification);
      }
    }, 1500);
  };

  const handleActionClick = (path) => {
    if (path) {
      onClose();
      navigate(path);
    }
  };

  const markAsRead = (id) => {
    // In a real app, this would update the notification status in the database
    log('Marking notification as read:', id);
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <AlertTriangle size={16} className="text-red-500" />;
      case 'medium': return <AlertCircle size={16} className="text-amber-500" />;
      case 'low': return <Info size={16} className="text-blue-500" />;
      case 'info': return <Info size={16} className="text-green-500" />;
      default: return <Info size={16} className="text-gray-500" />;
    }
  };

  const renderMainView = () => (
    <>
      <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
        <div className="flex items-center space-x-2">
          <select 
            className="text-xs border border-gray-200 rounded-md px-2 py-1"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="campaign">Campaigns</option>
            <option value="pricing">Pricing</option>
            <option value="system">System</option>
            <option value="performance">Performance</option>
          </select>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification) => (
            <div 
              key={notification.id} 
              className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${notification.unread ? 'bg-purple-50' : ''}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex items-start">
                <div className="mr-3 mt-0.5">{notification.icon}</div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <p className={`text-sm font-medium ${notification.unread ? 'text-gray-900' : 'text-gray-700'}`}>
                      {notification.title}
                    </p>
                    <span className="text-xs text-gray-500">{notification.timestamp}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                  
                  {notification.relatedKPI && (
                    <div className="mt-1 flex items-center">
                      <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full">
                        {notification.relatedKPI}: {notification.relatedKPIValue}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="px-4 py-6 text-center text-gray-500">
            <p>No notifications match your filter</p>
          </div>
        )}
      </div>
      <div className="px-4 py-2 border-t border-gray-100 flex justify-between items-center bg-gray-50">
        <span className="text-xs text-gray-500">Showing {filteredNotifications.length} of {notifications.length}</span>
        <button className="text-xs text-purple-600 hover:text-purple-900">Mark all as read</button>
      </div>
    </>
  );

  const renderDetailView = () => {
    if (!activeNotification) return null;
    
    const { detailData } = activeNotification;
    
    return (
      <>
        <div className="px-4 py-2 border-b border-gray-100 flex items-center">
          <button onClick={handleBack} className="mr-2 text-gray-500 hover:text-gray-700">
            <ChevronLeft size={16} />
          </button>
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            {activeNotification.icon}
            <span className="ml-2">{activeNotification.title}</span>
          </h3>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
        
        <div className="px-4 py-3">
          <div className={`p-3 rounded-md mb-4 ${
            activeNotification.severity === 'high' ? 'bg-red-50' : 
            activeNotification.severity === 'medium' ? 'bg-amber-50' : 
            activeNotification.severity === 'low' ? 'bg-blue-50' : 'bg-green-50'
          }`}>
            <p className="text-sm text-gray-800">{activeNotification.message}</p>
            <p className="text-xs text-gray-500 mt-2 flex items-center">
              <Clock size={12} className="mr-1" />
              {activeNotification.timestamp}
            </p>
            
            {activeNotification.relatedKPI && (
              <div className="mt-2 flex items-center">
                <span className="text-xs font-medium">{activeNotification.relatedKPI}:</span>
                <span className="text-xs ml-1">{activeNotification.relatedKPIValue}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            {activeNotification.type === 'alert' && (
              <>
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Campaign Performance</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-500">Expected ROI</p>
                      <p className="text-sm font-medium">{detailData.expectedROI}</p>
                    </div>
                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-500">Actual ROI</p>
                      <p className="text-sm font-medium text-red-600">{detailData.actualROI}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Underperforming Locations</h4>
                  <ul className="bg-gray-50 rounded divide-y divide-gray-100">
                    {detailData.underperformingLocations.map((location, idx) => (
                      <li key={idx} className="px-3 py-2 text-sm">{location}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}
            
            {activeNotification.type === 'competitor' && (
              <>
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Competitor Action</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-500">Competitor</p>
                      <p className="text-sm font-medium">{detailData.competitor}</p>
                    </div>
                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-500">Price Change</p>
                      <p className="text-sm font-medium text-amber-600">{detailData.priceDecrease}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Market Impact</h4>
                  <div className="bg-gray-50 p-3 rounded text-sm">
                    <p><strong>Affected Areas:</strong> {detailData.affectedAreas} locations</p>
                    <p><strong>Products:</strong> {detailData.targetedProducts.join(', ')}</p>
                    <p><strong>Impact Level:</strong> {detailData.marketImpact}</p>
                  </div>
                </div>
              </>
            )}
            
            {activeNotification.type === 'system' && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Maintenance Details</h4>
                <div className="bg-gray-50 p-3 rounded text-sm space-y-2">
                  <p><strong>Window:</strong> {detailData.maintenanceWindow}</p>
                  <p><strong>Impacted Systems:</strong> {detailData.impactedSystems.join(', ')}</p>
                  <p><strong>Backup:</strong> {detailData.backupAvailability}</p>
                  <p><strong>Contact:</strong> {detailData.contactInfo}</p>
                </div>
              </div>
            )}
            
            {activeNotification.type === 'performance' && (
              <>
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Achievement Details</h4>
                  <div className="bg-gray-50 p-3 rounded text-sm space-y-2">
                    <p><strong>Segment:</strong> {detailData.segment}</p>
                    <p><strong>Performance:</strong> {detailData.achievement}</p>
                    <p><strong>Growth:</strong> {detailData.growth}</p>
                    <p><strong>Status:</strong> {detailData.bonusStatus}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Top Performers</h4>
                  <ul className="bg-gray-50 rounded divide-y divide-gray-100">
                    {detailData.topPerformers.map((location, idx) => (
                      <li key={idx} className="px-3 py-2 text-sm">{location}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}
            
            {activeNotification.detailData?.recommendedActions && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Recommended Actions</h4>
                <ul className="bg-gray-50 rounded divide-y divide-gray-100">
                  {activeNotification.detailData.recommendedActions.map((action, idx) => (
                    <li key={idx} className="px-3 py-2 text-sm">{action}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        
        <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center bg-gray-50">
          {detailData?.actionWorkflow ? (
            <button 
              onClick={handleActionStart}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center"
            >
              <span>Take Action</span>
              <ChevronRight size={16} className="ml-1" />
            </button>
          ) : (
            <div className="flex w-full space-x-2">
              <button 
                onClick={() => markAsRead(activeNotification.id)}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 px-4 rounded-md text-sm font-medium transition-colors"
              >
                Mark as Read
              </button>
              <button 
                onClick={handleBack}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderActionView = () => {
    if (!activeNotification?.detailData?.actionWorkflow) return null;
    
    const workflow = activeNotification.detailData.actionWorkflow;
    const currentStep = workflow.steps[actionStep - 1];
    const isLastStep = actionStep === workflow.steps.length;
    
    return (
      <>
        <div className="px-4 py-2 border-b border-gray-100 flex items-center">
          <button onClick={handleBack} className="mr-2 text-gray-500 hover:text-gray-700">
            <ChevronLeft size={16} />
          </button>
          <h3 className="text-sm font-medium text-gray-900">Action Steps</h3>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
        
        <div className="px-4 py-3">
          {/* Progress steps */}
          <div className="mb-6">
            <div className="flex items-center w-full">
              {workflow.steps.map((step, idx) => (
                <React.Fragment key={idx}>
                  <div className="flex flex-col items-center relative">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      idx + 1 < actionStep ? 'bg-green-500 text-white' :
                      idx + 1 === actionStep ? 'bg-purple-600 text-white' :
                      'bg-gray-200 text-gray-500'
                    }`}>
                      {idx + 1 < actionStep ? (
                        <CheckCircle size={16} />
                      ) : (
                        <span className="text-xs">{idx + 1}</span>
                      )}
                    </div>
                    <span className="text-xs mt-1 text-center w-16 truncate">{step.title}</span>
                  </div>
                  
                  {idx < workflow.steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      idx + 1 < actionStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          
          {/* Current step content */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
            <h4 className="text-base font-medium text-gray-900 mb-2">{currentStep.title}</h4>
            <p className="text-sm text-gray-600 mb-4">{currentStep.description}</p>
            
            <div className="space-y-4">
              {currentStep.fields.map((field, idx) => (
                <div key={idx} className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  
                  {field.type === 'select' && (
                    <select 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      defaultValue={field.options[0]}
                    >
                      {field.options.map((option, optIdx) => (
                        <option key={optIdx} value={option}>{option}</option>
                      ))}
                    </select>
                  )}
                  
                  {field.type === 'multiselect' && (
                    <div className="flex flex-wrap gap-2">
                      {field.options.map((option, optIdx) => (
                        <div 
                          key={optIdx}
                          className={`text-xs px-2 py-1 rounded-full cursor-pointer ${
                            field.selected?.includes(option) 
                              ? 'bg-purple-100 text-purple-800 border border-purple-300' 
                              : 'bg-gray-100 text-gray-800 border border-gray-200'
                          }`}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {field.type === 'checkbox' && (
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500" 
                        defaultChecked={field.checked} 
                      />
                      <span className="ml-2 text-sm text-gray-700">{field.label}</span>
                    </div>
                  )}
                  
                  {field.type === 'radio' && (
                    <div className="space-y-2">
                      {field.options.map((option, optIdx) => (
                        <div key={optIdx} className="flex items-center">
                          <input 
                            type="radio" 
                            name={`${field.label}-radio`}
                            className="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500" 
                            defaultChecked={optIdx === 0} 
                          />
                          <span className="ml-2 text-sm text-gray-700">{option}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {field.type === 'range' && (
                    <div className="space-y-2">
                      <input 
                        type="range" 
                        min={field.min} 
                        max={field.max} 
                        defaultValue={field.value}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" 
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{field.min}{field.unit}</span>
                        <span>{field.value}{field.unit}</span>
                        <span>{field.max}{field.unit}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center bg-gray-50">
          <button 
            onClick={handlePrevStep}
            disabled={actionStep === 1}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              actionStep === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Previous
          </button>
          
          <div className="text-xs text-gray-500">
            Step {actionStep} of {workflow.steps.length}
          </div>
          
          <button 
            onClick={handleNextStep}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              isProcessing 
                ? 'bg-purple-400 cursor-not-allowed' 
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="flex items-center">
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Processing...
              </span>
            ) : (
              <span>{isLastStep ? 'Complete' : 'Next'}</span>
            )}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg py-1 z-30">
      {activeView === 'main' && renderMainView()}
      {activeView === 'detail' && renderDetailView()}
      {activeView === 'action' && renderActionView()}
    </div>
  );
};

export default NotificationPanel;
